import {
  DatabasesDisplayGridRowData,
  DatabasesDisplayPlacePageData,
  DatabaseTablesDisplayGridRowData,
  GraphDataQualityWarning,
  TablesDisplayPlacePageData
} from '@tableau/data-catalog-client-components';
import { IUserName } from '@tableau/vizportal-webclient-api/lib/api/types/IUser';
import { Omit } from '../helpers/TypescriptHelpers';
import { IContentInfo } from '../models/IContentInfo';
import { IDataQualityIndicatorInfo } from '../models/IDataQualityIndicatorInfo';
import { IUserInfo } from '../models/IUserInfo';

type DataQualityWarningSansAsset = Omit<GraphDataQualityWarning, 'asset'>;

export type GraphDatabase = DatabasesDisplayGridRowData | DatabasesDisplayPlacePageData;
export type GraphTable = DatabaseTablesDisplayGridRowData | TablesDisplayPlacePageData;

/**
 * There are three places that graph types need to be mapped to vizportalclient types for content actions:
 * 1. [ListViewGridColumnGenerator.graphActionCellWrapper] - to be passed to catalog so it can map the single item on list view
 * 2. [renderDatabasesActionMenu] and [renderTablesActionMenu] - to be passed to catalog so it can map the single item on place page
 * 3. [ExplorerToolbar] which catalog uses by way of [Explorer] <- [ConnectedExplorer] <- [CatalogBrowserSubspaceExplorer]
 *    somehow we've convinced the explorer that the items being selected in the explorer when on database/tables list are 
 *    [IExplorableItemTypeInfo] and it would be preferable to map wherever that is, instead of lying to the type system.
 */
export function mapGraphTypeToIContentInfo(content: GraphDatabase | GraphTable): IContentInfo | null {
  if (content.__typename === 'CustomSQLTable') {
    return null;
  }

  /**
   * This check for dataQualityIndicators needs to be here since we can get a graph type into an action menu 
   * so many different ways we usually end up double mapping and we need to prevent that.
   * I believe I've removed all of the double mapping but am keeping this here in case it's accidentally done again.
   */
  if (!!(content as any as IContentInfo).dataQualityIndicators) {
    return content as any as IContentInfo;
  }

  const certifier: IUserInfo | null = !content.certifier ? null : {
    id: null,
    username: content.certifier.username,
    displayName: content.certifier.name,
  };

  const contentType = content.__typename === 'DatabaseTable' ? 'table' : 'database';
  const dataQualityIndicator = mapGraphDataQualityWarningToVizportalDataQualityIndicator(content.dataQualityWarning);
  const dataQualityIndicators = dataQualityIndicator ? [dataQualityIndicator] : null;

  return {
    certifier,
    contentType,
    dataQualityIndicators,
    id: content.id,
    vizportalId: content.vizportalId,
    name: content.name,
    description: content.description,
    favorite: false,
    updatedAt: new Date(),
    certificationNote: content.certificationNote,
    isCertified: content.isCertified,
  };
}

function mapGraphDataQualityWarningToVizportalDataQualityIndicator(dataQualityWarning?: DataQualityWarningSansAsset): IDataQualityIndicatorInfo | undefined {
  if (!dataQualityWarning) {
    return;
  }

  const id = dataQualityWarning.vizportalId;
  const owner = mapAuthorToOwner(dataQualityWarning);
  const type = dataQualityWarning.warningType;

  return {
    id,
    owner,
    type,
    isActive: dataQualityWarning.isActive,
    message: dataQualityWarning.message,
    updatedAt: dataQualityWarning.updatedAt
  };
}

function mapAuthorToOwner(warning: DataQualityWarningSansAsset): IUserName {
  const author = warning.author;

  const username = !!author ? author.username : '';
  const fallbackDisplayName = warning.authorDisplayName || '';
  const displayName = !!author && !!author.name ? author.name : fallbackDisplayName;

  return {
    id: '',
    authId: '',
    displayName,
    username,
  };
}
